import { API } from '@/http-common';
import {
    buildDate,
    buildPifSignatureDocumentStatut,
    buildProduitTypeLabel,
    buildProduitTypeShortcut
} from '@/stores/utils';
import { Filters } from '@/types/participant/formations-filters-types';
import {
    Formation,
    FormationResponse
} from '@/types/participant/formations-types';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { LocationQuery } from 'vue-router';

const noFilterState: Filters = {
    produitId: undefined,
    date: undefined,
    datePeriodeId: undefined,
    datePeriodeDebut: undefined,
    datePeriodeFin: undefined,
    action: undefined,
    typeId: undefined,
    affichage: undefined
};

const filterInitialState: Filters = {
    produitId: undefined,
    date: undefined,
    datePeriodeId: undefined,
    datePeriodeDebut: undefined,
    datePeriodeFin: undefined,
    action: undefined,
    typeId: undefined,
    affichage: 'FORMATIONS_EN_COURS-A_VENIR'
};

export const FORMATIONS_FILTERS_CONFIGURATION = {
    produitId: {
        name: 'produitId',
        isArray: false
    },
    date: {
        name: 'date',
        isArray: false
    },
    datePeriodeId: {
        name: 'datePeriodeId',
        isArray: false
    },
    datePeriodeDebut: {
        name: 'datePeriodeDebut',
        isArray: false
    },
    datePeriodeFin: {
        name: 'datePeriodeFin',
        isArray: false
    },
    typeId: {
        name: 'typeId',
        isArray: false
    },
    action: {
        name: 'action',
        isArray: false
    },
    affichage: {
        name: 'affichage',
        isArray: false
    }
};

export const useFormationFilterStore = defineStore('formations-filter', () => {
    const filters = ref<Filters>(filterInitialState);

    const setFilter = (name: string, value: any) => {
        filters.value = { ...filters.value, [name]: value };
    };

    const resetFilters = () => {
        filters.value = noFilterState;
    };

    const parsePayload = (payload: LocationQuery): Filters => {
        const newPayload = { ...payload };

        Object.keys(newPayload).forEach((key) => {
            const filter = FORMATIONS_FILTERS_CONFIGURATION[key] as any;

            if (filter && filter.isArray) {
                newPayload[key] = newPayload[key].split(',');
            }
            if (
                [
                    FORMATIONS_FILTERS_CONFIGURATION.date.name,
                    FORMATIONS_FILTERS_CONFIGURATION.datePeriodeDebut.name,
                    FORMATIONS_FILTERS_CONFIGURATION.datePeriodeFin.name
                ].includes(key)
            ) {
                newPayload[key] = new Date(newPayload[key]);
            }
        });

        return newPayload;
    };

    const initFiltersFromQuery = (payload: LocationQuery) => {
        const parsedPayload = parsePayload(payload) as Filters;
        if (payload && Object.keys(payload).length > 0) {
            filters.value = {
                ...noFilterState,
                ...parsedPayload
            };
        } else {
            filters.value = {
                ...filterInitialState,
                ...parsedPayload
            };
        }
    };

    return {
        resetFilters,
        setFilter,
        filters,
        initFiltersFromQuery
    };
});

export const useFormationStore = defineStore('formations', () => {
    const formations = ref<Formation[]>([]);

    const fetchFormations = async (query: Filters) => {
        const response = await API.get(`participant/v2/formations`, {
            params: query
        });
        if (response?.data) {
            formations.value = response?.data.map((i: FormationResponse) =>
                mapFormation(i)
            );
        }
    };

    const mapFormation = (f: FormationResponse): Formation => ({
        ...f,
        typeShortcut: buildProduitTypeShortcut(
            f.type.id,
            f.produitLienElearning
        ),
        ...(f.pif?.id &&
            f.pif.typeSignatureId == 1 && {
                pifStatut: buildPifSignatureDocumentStatut(
                    f.pif?.disponible,
                    f.pif?.signee,
                    f.pif?.validee,
                    f.pif?.pending,
                    f.pif?.pendingParticipant
                )
            }),
        typeLabel: buildProduitTypeLabel(f.type.label, f.produitLienElearning),
        statutLabel:
            f?.statut?.id === -1 ? 'Partiellement confirmé' : f?.statut?.label,
        datesFormations: buildDate(f.dateDebut, f.dateFin),
        sessionStatutLabel: f.sessionStatut?.label,
        disabled: f.sessionStatut?.id === 5
    });
    return {
        formations,
        fetchFormations
    };
});
